/**
 * DateTime utility functions for ORO app
 * 
 * Provides consistent date/time formatting, validation, and manipulation
 * across the application. Follows established patterns from EventHeader.tsx
 */

/**
 * Formats a timestamp for display in event headers and cards
 */
export function formatEventDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Formats a timestamp for time display
 */
export function formatEventTime(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Formats timestamp for compact display (used in cards)
 */
export function formatCompactDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diffDays = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays < 7 && diffDays > 0) return date.toLocaleDateString('en-US', { weekday: 'long' });
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Formats time for compact display
 */
export function formatCompactTime(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Formats date range for events
 */
export function formatDateRange(startTimestamp: number, endTimestamp?: number): string {
  const startDate = formatCompactDate(startTimestamp);
  const startTime = formatCompactTime(startTimestamp);
  
  if (!endTimestamp) {
    return `${startDate} at ${startTime}`;
  }
  
  const endDate = formatCompactDate(endTimestamp);
  const endTime = formatCompactTime(endTimestamp);
  
  // Same day event
  if (startDate === endDate) {
    return `${startDate}, ${startTime} - ${endTime}`;
  }
  
  // Multi-day event
  return `${startDate} ${startTime} - ${endDate} ${endTime}`;
}

/**
 * Checks if an event is happening today
 */
export function isToday(timestamp: number): boolean {
  const date = new Date(timestamp * 1000);
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

/**
 * Checks if an event is in the past
 */
export function isPast(timestamp: number): boolean {
  const now = Date.now();
  return timestamp * 1000 < now;
}

/**
 * Checks if an event is upcoming (in future)
 */
export function isUpcoming(timestamp: number): boolean {
  return !isPast(timestamp);
}

/**
 * Gets relative time string (e.g., "in 2 hours", "3 days ago")
 */
export function getRelativeTime(timestamp: number): string {
  const now = Date.now();
  const eventTime = timestamp * 1000;
  const diffMs = eventTime - now;
  const diffMinutes = Math.floor(Math.abs(diffMs) / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  const isInPast = diffMs < 0;
  const suffix = isInPast ? 'ago' : 'from now';
  
  if (diffMinutes < 1) return 'now';
  if (diffMinutes < 60) return `${diffMinutes}m ${suffix}`;
  if (diffHours < 24) return `${diffHours}h ${suffix}`;
  return `${diffDays}d ${suffix}`;
}

/**
 * Converts Date to timestamp (seconds)
 */
export function dateToTimestamp(date: Date): number {
  return Math.floor(date.getTime() / 1000);
}

/**
 * Converts timestamp (seconds) to Date
 */
export function timestampToDate(timestamp: number): Date {
  return new Date(timestamp * 1000);
}

/**
 * Gets smart default time for new events (next hour, rounded)
 */
export function getDefaultEventTime(): Date {
  const now = new Date();
  const nextHour = new Date(now);
  nextHour.setHours(now.getHours() + 1, 0, 0, 0); // Round to next hour
  return nextHour;
}

/**
 * Validates that end time is after start time
 */
export function validateTimeRange(startTime: Date, endTime?: Date): string | null {
  if (!endTime) return null;
  
  if (endTime <= startTime) {
    return 'End time must be after start time';
  }
  
  const diffHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
  if (diffHours > 24) {
    return 'Event duration cannot exceed 24 hours';
  }
  
  return null;
}